import { PartnerContext } from 'pages/nos-partenaires/[slug]';
import React, { ComponentPropsWithRef, FC, useContext, useMemo } from 'react';
import { FeaturedNews } from '@actus/components/FeaturedNews/FeaturedNews.component';
import { ADVERT_PAGE_ID } from '@adverts/business/Adverts.contants';
import { useAdvert } from '@adverts/business/useAdvert';
import { BreadCrumb } from '@components/BreadCrumb/BreadCrumb.component';
import { BreadCrumbStep } from '@components/BreadCrumb/BreadCrumb.types';
import { Address } from '@components/content/Address/Address.component';
import { ContentBody } from '@components/content/ContentBody/ContentBody.component';
import { PlcFlatRectLoader } from '@components/Loaders/PlcFlatRectLoader.component';
import { PlcImgLoader } from '@components/Loaders/PlcImgLoader.component';
import { PlcTextListLoader } from '@components/Loaders/PlcTextLoader.component';
import { PlcImg } from '@components/PlcImg/PlcImg.component';
import { PlcMessageWithRetry } from '@components/PlcMessage/PlcMessageWithRetry.component';
import { Text } from '@components/Text/Text.component';
import { PlcPageLayout } from '@layout/components/PlcPageLayout/PlcPageLayout.component';
import { Spacing } from '@styles/Spacing.style';
import { getAddressInfos } from '@utils/strings';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';
import { ATLevel2, PlcTagProps } from 'features/tagPlan/business/TagPlan.types';
import styles from './PartnerLayout.module.scss';

type Props = ComponentPropsWithRef<'div'> & {};

export const PartnerLayout: FC<Props> = ({ className, ...rest }) => {
  const {
    partner: { data: partner, isValidating, retryCallback },
    lastActus: { data: lastActus, isValidating: lastActusLoading },
  } = useContext(PartnerContext);

  const rootClass = classnames(styles['partner-layout'], ['plc-pt-m plc-page wrapper-s'], className);

  useAdvert({
    pageId: ADVERT_PAGE_ID.ServicesAffiliate,
  });

  const tagProps: PlcTagProps = useMemo(
    () => ({
      isDataLoading: isValidating,
      level2: ATLevel2.Partners,
      tagPageNameI18nArray: ['partners.tag.title', partner ? { code: partner.slug, notI18n: true } : undefined],
    }),
    [isValidating, partner]
  );

  if (!partner) {
    return (
      <div className={rootClass} {...rest}>
        <main>
          {isValidating ? (
            <>
              <div className="flex column align-start row-up-m space-between-up-m">
                <PlcTextListLoader
                  fixedWidth
                  textProps={[
                    { bottomSpacing: Spacing.xs, numberOfLine: 1, variant: 'h2', width: 400 },
                    { bottomSpacing: Spacing.m, numberOfLine: 1, variant: 'h3', width: 500 },
                  ]}
                  uniqueKey="partner-textlist"
                />
                <PlcImgLoader className="plc-mb-m" height={100} uniqueKey="partner-img" width={300} withSun={false} />
              </div>
              <PlcFlatRectLoader className="plc-mb-m" fixedWidth height={100} uniqueKey="partner-rect" width={300} />
              <PlcTextListLoader
                textProps={[{ bottomSpacing: Spacing.xl, numberOfLine: 7, variant: 'p', width: 600 }]}
                uniqueKey="partner-bottom-textlist"
              />
            </>
          ) : (
            <PlcMessageWithRetry retryCallback={retryCallback} />
          )}
        </main>
      </div>
    );
  }

  const { metaTitle, metaDescription, name, description, image, body, address, websiteUrl, email, phoneNumber } =
    partner;
  const addressInfos = address && getAddressInfos(address);
  const showAddress = addressInfos || websiteUrl || email || phoneNumber;

  const breadcrumbSteps: BreadCrumbStep[] = [
    {
      label: 'common.newspaper-short',
      linkProps: { as: urlEnum.home, href: urlEnum.home },
    },
    {
      label: 'common.partners',
      linkProps: { as: urlEnum.partners, href: urlEnum.partners },
    },
    {
      label: name,
      notI18n: true,
    },
  ];

  return (
    <PlcPageLayout
      className={rootClass}
      metaDescI18n={metaDescription || description}
      metaTitleI18n={metaTitle || name}
      tagProps={tagProps}
      {...rest}
    >
      <BreadCrumb steps={breadcrumbSteps} />
      <section className="plc-mb-l plc-mt-l plc-mb-up-m-xl">
        <div className="flex column align-start row-up-m space-between-up-m">
          <div className="plc-mr-up-m-xxl">
            <Text className="plc-mb-xs" tag="h1" variant="h2">
              {name}
            </Text>
            {description && (
              <Text className="plc-mb-m" tag="h2" variant="h3">
                {description}
              </Text>
            )}
          </div>
          <div className={`${styles['partner-layout-image']} plc-mb-m`}>
            <PlcImg alt={image.alt} plcSrc={{ src: image.url, type: 'external' }} />
          </div>
        </div>
        {showAddress && (
          <Address
            addressInfos={addressInfos}
            className="border-bottom plc-pb-l plc-mb-l"
            email={email}
            infoVariant="p"
            phone={phoneNumber}
            website={websiteUrl}
          />
        )}
        {body && <ContentBody body={body} className="plc-mb-l plc-mb-up-m-xl" isPartial={false} />}
      </section>
      <FeaturedNews className="plc-pt-l plc-pt-up-m-xl plc-px-0" isLoading={lastActusLoading} news={lastActus || []} />
    </PlcPageLayout>
  );
};
